import React, { useMemo, useEffect, useCallback, useState, useRef, memo } from 'react';
import { throttle } from 'lodash';
import { Map, Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import { Box, LinearProgress, IconButton, Typography, Badge } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import 'maplibre-gl/dist/maplibre-gl.css';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import { useMapState } from '../../../hooks/useMapState.js';
import { useMapSync } from './map_syncro.js';
import { getISOWeekAndYear } from '../../../utils/dateUtils.js';
import MapSynchronizer from './sync_maps.js';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import {
  generateGeoJSONFileName,
  THROTTLE_CONFIG,
  TILE_LAYER_URL,
  BOUNDS_RECTANGLE_STYLE,
  MASK_POLYGON_STYLE,
  MAP_CONFIG
} from '../../../utils/mapUtils.js';
import LegendMap from './map_legend.js';
import MetricsInsideMap from '../metrics_inside_map.js';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';

// ─── Register PMTiles protocol once (safe to call multiple times) ──────────
let _pmtilesRegistered = false;
if (!_pmtilesRegistered) {
  try {
    const protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile.bind(protocol));
    _pmtilesRegistered = true;
  } catch (e) {
    _pmtilesRegistered = true;
  }
}

function buildPredFilename(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `pred_${d.getUTCFullYear()}` +
    `${pad(d.getUTCMonth() + 1)}` +
    `${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}` +
    `${pad(d.getUTCMinutes())}` +
    `${pad(d.getUTCSeconds())}Z.pmtiles`
  );
}

function extractIssueHour(issueTime) {
  if (!issueTime) return '01';
  const m = issueTime.match(/[T\s](\d{2}):/);
  return m ? m[1] : '01';
}

// ─── Inner memo component ────────────────────────────
const MapLeftContent = memo(({
  mapLeftLayer,
  geoJsonData,
  loading,
  hasData,
  rectBounds,
  maskPolygon,
  cqlFecha,
  selectedTime,
  isInteractive,
  showMosaic,
  showGeoJson,
  pmtilesHistoricalUrl,
}) => {
  const { current: map } = useMap();
  const [hoverInfo, setHoverInfo] = useState(null);
  const isMoving = useRef(false);

  useEffect(() => {
    if (!map) return;
    const nativeMap = map.getMap();

    const onMouseMoveNative = throttle((e) => {
      if (isMoving.current) return;
      const features = nativeMap.queryRenderedFeatures(e.point, { layers: ['cnn-layer'] });
      if (features && features[0]) {
        setHoverInfo({ feature: features[0], lngLat: e.lngLat });
      } else {
        setHoverInfo(null);
      }
    }, 150);

    const onMouseLeaveNative = () => {
      setHoverInfo(null);
      onMouseMoveNative.cancel();
    };

    const handleMovementStart = () => { isMoving.current = true; setHoverInfo(null); };
    const handleMovementEnd = () => { isMoving.current = false; };

    nativeMap.on('mousemove', 'cnn-layer', onMouseMoveNative);
    nativeMap.on('mouseleave', 'cnn-layer', onMouseLeaveNative);
    nativeMap.on('movestart', handleMovementStart);
    nativeMap.on('moveend', handleMovementEnd);
    nativeMap.on('zoomstart', handleMovementStart);
    nativeMap.on('zoomend', handleMovementEnd);

    return () => {
      nativeMap.off('mousemove', 'cnn-layer', onMouseMoveNative);
      nativeMap.off('mouseleave', 'cnn-layer', onMouseLeaveNative);
      nativeMap.off('movestart', handleMovementStart);
      nativeMap.off('moveend', handleMovementEnd);
      nativeMap.off('zoomstart', handleMovementStart);
      nativeMap.off('zoomend', handleMovementEnd);
      onMouseMoveNative.cancel();
    };
  }, [map]);

  const cnnLayerStyle = useMemo(() => ({
    id: 'cnn-layer',
    type: 'fill',
    paint: {
      'fill-color': [
        'match',
        ['get', 'CONVECTIVE_MATRIX_RISK'],
        3, '#DC143C',
        2, '#FF8C00',
        1, '#ffff00',
        'transparent'
      ],
      'fill-opacity': 0.75,
      'fill-outline-color': '#a3a1a1c0'
    }
  }), []);

  const mapBounds = useMemo(() => {
    if (!rectBounds) return null;
    return [rectBounds[0][1], rectBounds[0][0], rectBounds[1][1], rectBounds[1][0]];
  }, [rectBounds]);

  useEffect(() => {
    if (map && mapBounds) {
      map.fitBounds(mapBounds, { padding: 60, duration: 800 });
    }
  }, [map, mapBounds]);

  const maskGeoJson = useMemo(() => {
    if (!maskPolygon) return null;
    const [outer, inner] = maskPolygon;
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          outer.map(([lat, lon]) => [lon, lat]),
          inner.map(([lat, lon]) => [lon, lat])
        ]
      }
    };
  }, [maskPolygon]);

  return (
    <>
      <MapSynchronizer />
      {showMosaic && pmtilesHistoricalUrl && (
        <Source
          id="pmtiles-source"
          type="raster"
          url={`pmtiles://${pmtilesHistoricalUrl}`}
          tileSize={256}
        >
          <Layer
            id="pmtiles-layer"
            type="raster"
            beforeId={maskPolygon ? 'mask-layer' : (geoJsonData ? 'cnn-layer' : undefined)}
            paint={{
              'raster-opacity': 0.8,
              'raster-fade-duration': 0
            }}
          />
        </Source>
      )}

      {showGeoJson && geoJsonData && (
        <Source type="geojson" data={geoJsonData}>
          <Layer {...cnnLayerStyle} />
        </Source>
      )}

      {maskGeoJson && (
        <Source type="geojson" data={maskGeoJson}>
          <Layer
            id="mask-layer"
            type="fill"
            paint={{
              'fill-color': MASK_POLYGON_STYLE.fillColor,
              'fill-opacity': MASK_POLYGON_STYLE.fillOpacity
            }}
          />
        </Source>
      )}

      {rectBounds && (
        <Source type="geojson" data={{
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [rectBounds[0][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[0][0]]
            ]
          }
        }}>
          <Layer
            id="bounds-layer"
            type="line"
            paint={{
              'line-color': BOUNDS_RECTANGLE_STYLE.color,
              'line-width': BOUNDS_RECTANGLE_STYLE.weight,
              'line-dasharray': [3, 5]
            }}
          />
        </Source>
      )}

      {hoverInfo && (
        <Popup
          longitude={hoverInfo.lngLat.lng}
          latitude={hoverInfo.lngLat.lat}
          closeButton={false}
          anchor="bottom"
        >
          <div style={{
            padding: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderRadius: '16px',
            color: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
            fontFamily: "'Inter', sans-serif",
            fontSize: '13px',
            minWidth: '150px'
          }}>
            <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
              Storm Risk
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                backgroundColor: [
                  'transparent',
                  '#ffff00',
                  '#FFA500',
                  '#DC143C'
                ][hoverInfo.feature.properties.CONVECTIVE_MATRIX_RISK] || '#ccc',
                border: '1px solid rgba(0,0,0,0.1)'
              }} />
              <strong style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '-0.02em' }}>
                {[
                  '',
                  'Low',
                  'Medium',
                  'High'
                ][hoverInfo.feature.properties.CONVECTIVE_MATRIX_RISK] || 'Unknown'}
              </strong>
            </div>
          </div>
        </Popup>
      )}
    </>
  );
});

const MapLeft = ({
  cqlSevere,
  cqlFecha,
  mapLeftLayer,
  issueTime,
  weekNumber,
  selectedTime,
  isInteractive = true,
  metricsName,
  handleRiskClick,
}) => {
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [showMosaic, setShowMosaic] = useState(true);
  const [showGeoJson, setShowGeoJson] = useState(false);
  const [historicalConfig, setHistoricalConfig] = useState({ '025': null, '01': null });
  const { mapState } = useMapSync();
  const mapRef = useRef(null);

  useEffect(() => {
    setShowGeoJson(false);
  }, [mapLeftLayer]);

  const {
    loading,
    showLegend,
    hasData,
    rectBounds,
    maskPolygon,
    setLoading,
    setHasData,
    handleToggleLegend,
    handleResetView,
    createThrottledFunction,
    createDebouncedFunction,
    selectedModel,
  } = useMapState(mapLeftLayer);

  // Función manual para resetear el zoom
  const handleReset = () => {
    if (mapRef.current && rectBounds) {
      const bounds = [rectBounds[0][1], rectBounds[0][0], rectBounds[1][1], rectBounds[1][0]];
      mapRef.current.fitBounds(bounds, { padding: 60, duration: 1000 });
    }
  };

  useEffect(() => {
    const fetchBaseUrls = async () => {
      try {
        const [res025, res01] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/pmtiles/historical-cnn-025`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/pmtiles/historical-cnn-01`)
        ]);
        setHistoricalConfig({
          '025': res025.data.baseUrl,
          '01': res01.data.baseUrl
        });
      } catch (err) {
        console.warn('[MapLeft] Could not fetch historical base URLs:', err.message);
      }
    };
    fetchBaseUrls();
  }, []);

  // ── Build PMTiles URL whenever controls change ─────────────────────────
  // URL: {baseUrl}/{year}/{month}/{day}/{issue_hour}/pred_YYYYMMDDTHHMMSSZ.pmtiles
  //
  // • issueTime  = "2025-04-30 01:00:00"  → year/month/day and issue_hour (01)
  // • cqlFecha   = "2025-04-30 07:00:00"  → prediction filename
  const pmtilesHistoricalUrl = useMemo(() => {
    const is025 = mapLeftLayer && mapLeftLayer.includes('025');
    const is01 = mapLeftLayer && mapLeftLayer.includes('01');
    if (!showMosaic || (!is025 && !is01) || !cqlFecha || !issueTime) return null;

    const baseUrl = is025 ? historicalConfig['025'] : historicalConfig['01'];
    if (!baseUrl) return null;

    try {
      const issueDatePart = issueTime.split(/[T\s]/)[0];
      const [year, month, day] = issueDatePart.split('-');
      const issueHourStr = extractIssueHour(issueTime);
      const issueHour = parseInt(issueHourStr, 10);

      let validTimeStr = cqlFecha.replace(' ', 'T');
      if (!validTimeStr.endsWith('Z')) validTimeStr += 'Z';

      const predDate = new Date(validTimeStr);
      if (isNaN(predDate.getTime())) return null;

      if (predDate.getUTCHours() < issueHour) {
        predDate.setUTCHours(issueHour);
      }

      const filename = buildPredFilename(predDate);
      return `${baseUrl}/${year}/${month}/${day}/${issueHourStr}/${filename}`;
    } catch (e) {
      console.warn('[MapLeft] Error building PMTiles URL:', e);
      return null;
    }
  }, [showMosaic, mapLeftLayer, historicalConfig, cqlFecha, issueTime]);

  // ── GeoJSON URLs for WFS vector layers ───────────────────────────────
  const geoJsonUrls = useMemo(() => ({
    "CNN_HISTORICAL:EU_025_live_demo": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/cnn-forecast-metrics-primary?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:EU_025_live_demo&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
      backup: `${process.env.REACT_APP_API_URL}/proxy/cnn-forecast-metrics-primary?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:EU_025_live_demo&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
    },
    "CNN_HISTORICAL:EU_01_live_demo": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/cnn-forecast-metrics-primary?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:EU_01_live_demo&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
      backup: `${process.env.REACT_APP_API_URL}/proxy/cnn-forecast-metrics-backup?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:EU_01_live_demo&outputFormat=application/json&CQL_FILTER=VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`,
    },
    "CNN_HISTORICAL:euro_1k_00625_15m": {
      primary: `${process.env.REACT_APP_API_URL}/proxy/cnn-forecast-metrics-primary?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:euro_1k_00625_15m&outputFormat=application/json&CQL_FILTER=${encodeURI(`VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`)}`,
      backup: `${process.env.REACT_APP_API_URL}/proxy/cnn-forecast-metrics-backup?service=WFS&version=1.1.0&request=GetFeature&typeName=CNN_HISTORICAL:euro_1k_00625_15m&outputFormat=application/json&CQL_FILTER=${encodeURI(`VALID_TIMESTAMP='${cqlFecha}' AND ISSUE_TIMESTAMP='${issueTime}' AND WEEK_NUMBER='${weekNumber}'`)}`,
    },
  }), [cqlFecha, issueTime, weekNumber]);

  const fetchWithFallback = useCallback(async (urlObj, signal) => {
    if (!urlObj || !urlObj.primary || !urlObj.backup) return null;
    try {
      if (signal?.aborted) return null;
      const resp = await axios.get(urlObj.primary, { signal });
      if (resp.status === 200) return resp.data;
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      console.warn('Primary source failed, trying backup...', err.message);
    }

    if (signal?.aborted) return null;
    try {
      const backupResp = await axios.get(urlObj.backup, { signal });
      return backupResp.data;
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      console.error('Backup source also failed:', err.message);
      return null;
    }
  }, []);

  const abortControllerRef = useRef(null);

  const loadGeoJson = useCallback(async (urlObj) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setHasData(true);
    try {
      const data = await fetchWithFallback(urlObj, controller.signal);
      if (controller.signal.aborted) return;
      if (!data || !data.features) {
        setHasData(false);
        setGeoJsonData(null);
        return;
      }
      data.features.sort((a, b) => (Number(a.properties.CONVECTIVE_MATRIX_RISK) || 0) - (Number(b.properties.CONVECTIVE_MATRIX_RISK) || 0));
      setGeoJsonData(data);
      setHasData(data.features.length > 0);
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') return;

      console.error('❌ [MapLeft] Error cargando capa:', error);
      setHasData(false);
    } finally {
      if (abortControllerRef.current === controller) setLoading(false);
    }
  }, [fetchWithFallback, setLoading, setHasData]);

  const throttledLoad = useMemo(() => createThrottledFunction(loadGeoJson, THROTTLE_CONFIG.FETCH_INTERVAL), [loadGeoJson, createThrottledFunction]);
  const debouncedLoad = useMemo(() => createDebouncedFunction(loadGeoJson, THROTTLE_CONFIG.FETCH_INTERVAL), [loadGeoJson, createDebouncedFunction]);

  useEffect(() => {
    if (!mapLeftLayer) return;
    const urlObj = geoJsonUrls[mapLeftLayer];
    throttledLoad(urlObj);
    debouncedLoad(urlObj);
    return () => {
      throttledLoad.cancel();
      debouncedLoad.cancel();
    };
  }, [mapLeftLayer, geoJsonUrls, throttledLoad, debouncedLoad]);

  const mosaicFilters = useMemo(() => {
    if (!cqlFecha || !issueTime) return null;
    const datePart = cqlFecha.split(' ')[0];
    const { year, week } = getISOWeekAndYear(datePart);
    const forecastRelease = issueTime.includes(' ') ? issueTime.split(' ')[1].split(':')[0] : issueTime.split(':')[0]; // Extract "01" from "2024-07-30 01:00:00" or "01:00:00"

    // Ensure TIME is ISO with Z
    let timeIso = cqlFecha.trim().replace(' ', 'T');
    if (!timeIso.endsWith('Z')) timeIso += 'Z';

    return { year, week, forecastRelease, time: timeIso };
  }, [cqlFecha, issueTime]);

  const is025Layer = mapLeftLayer && mapLeftLayer.includes('025');
  const isHighResLayer = selectedModel && selectedModel.includes('euro_1k_00625_15m');
  const [isMenuOpen, setIsMenuOpen] = useState(false);


  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {loading && (
        <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1000 }}>
          <LinearProgress />
        </Box>
      )}

      <Map
        id="mapLeft"
        onLoad={(e) => { mapRef.current = e.target; }} // <--- AGREGADO
        initialViewState={{
          latitude: mapState.center[0],
          longitude: mapState.center[1],
          zoom: mapState.zoom
        }}
        mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={['cnn-layer']}
        dragPan={isInteractive}
        scrollZoom={isInteractive}
        boxZoom={isInteractive}
        doubleClickZoom={isInteractive}
        touchZoomRotate={isInteractive}
        minZoom={MAP_CONFIG.minZoom}
        maxZoom={MAP_CONFIG.maxZoom}
        fadeDuration={0}
        collectResourceTiming={false}
      >
        <MapLeftContent
          mapLeftLayer={mapLeftLayer}
          geoJsonData={geoJsonData}
          loading={loading}
          hasData={hasData}
          rectBounds={rectBounds}
          maskPolygon={maskPolygon}
          cqlFecha={cqlFecha}
          selectedTime={selectedTime}
          isInteractive={isInteractive}
          showMosaic={showMosaic}
          showGeoJson={showGeoJson}
          pmtilesHistoricalUrl={pmtilesHistoricalUrl}
        />
      </Map>

      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.9 }}>
        <Badge
          badgeContent={
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
              RESET ZOOM
            </Typography>
          }
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiBadge-badge': {
              padding: '0 8px',
              height: '18px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              top: '0px',
              left: '-2px',
              transform: 'translateX(-100%)',
              borderRadius: '10px',
            },
          }}
        >
          <IconButton
            onClick={handleReset}
            sx={{
              bgcolor: '#ffffff',
              color: '#38a477',
              '&:hover': { bgcolor: '#f5f5f5' },
              width: { xs: 28, md: 36 },
              height: { xs: 28, md: 36 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              borderRadius: 10,
              opacity: 0.9
            }}
            title="Reset view"
          >
            <HomeIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
          </IconButton>
        </Badge>

        {(is025Layer || (mapLeftLayer && mapLeftLayer.includes('01'))) && (
          <>
            <Badge
              badgeContent={
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                  HEATMAPS
                </Typography>
              }
              color="error"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  padding: '0 8px',
                  height: '18px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  top: '0px',
                  left: '-2px',
                  transform: 'translateX(-100%)',
                  borderRadius: '10px',
                },
              }}
            >
              <IconButton
                onClick={() => setShowMosaic(!showMosaic)}
                sx={{
                  bgcolor: showMosaic ? '#38a477' : '#ffffff',
                  color: showMosaic ? '#ffffff' : '#38a477',
                  '&:hover': { bgcolor: showMosaic ? '#38a477' : '#f5f5f5' },
                  width: { xs: 28, md: 36 },
                  height: { xs: 28, md: 36 },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: 10,
                  opacity: 0.9
                }}
                title="Toggle Heatmap Layer"
              >
                <LayersIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
              </IconButton>
            </Badge>

            <Badge
              badgeContent={
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                  POLYGONS
                </Typography>
              }
              color="error"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{
                '& .MuiBadge-badge': {
                  padding: '0 8px',
                  height: '18px',
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  top: '0px',
                  left: '-2px',
                  transform: 'translateX(-100%)',
                  borderRadius: '10px',
                },
              }}
            >
              <IconButton
                onClick={() => setShowGeoJson(!showGeoJson)}
                sx={{
                  bgcolor: showGeoJson ? '#38a477' : '#ffffff',
                  color: showGeoJson ? '#ffffff' : '#38a477',
                  '&:hover': { bgcolor: showGeoJson ? '#38a477' : '#f5f5f5' },
                  width: { xs: 28, md: 36 },
                  height: { xs: 28, md: 36 },
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: 10,
                  opacity: 0.9
                }}
                title="Toggle Polygons Layer"
              >
                <MapIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
              </IconButton>
            </Badge>
          </>
        )}

        <Box title='Metrics' sx={{ position: 'relative', display: 'block' }}>
          <Badge
            badgeContent={
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
                AI METRICS
              </Typography>
            }
            color="error"
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiBadge-badge': {
                padding: '0 8px',
                height: '18px',
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                top: '0px',
                left: '-2px',
                transform: 'translateX(-100%)',
                borderRadius: '10px',
              },
            }}
          >
            <IconButton
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              sx={{
                bgcolor: isMenuOpen ? '#38a477' : '#ffffff',
                color: isMenuOpen ? '#ffffff' : '#38a477',
                '&:hover': { bgcolor: isMenuOpen ? '#38a477' : '#f5f5f5' },
                width: { xs: 28, md: 36 },
                height: { xs: 28, md: 36 },
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                borderRadius: 10,
                opacity: 0.9
              }}
            >
              <QueryStatsIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
            </IconButton>
          </Badge>

          {isMenuOpen && (
            <Box sx={{
              position: 'absolute',
              top: 10,
              right: 40,
              width: '180px',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '15px',
              zIndex: 1100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 1,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, borderBottom: '1px solid #444' }}>
                METRICS
              </Typography>
              {metricsName && metricsName.length > 0 ? (
                <MetricsInsideMap
                  onMenuOpen={() => setIsMenuOpen(true)}
                  onMenuClose={() => setIsMenuOpen(true)}
                  metricsNames={metricsName}
                  cqlFecha={cqlFecha}
                  weekNumber={weekNumber}
                  issueTime={issueTime}
                  cqlSevere={cqlSevere}
                  handleRiskClick={handleRiskClick}
                />
              ) : (
                <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic' }}>
                  Loading metrics...
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box title='Information' sx={{ position: 'absolute', bottom: 15, right: 15, zIndex: 10 }}>
        <IconButton
          onClick={handleToggleLegend}
          sx={{
            bgcolor: '#ffffff',
            color: '#38a477',
            '&:hover': { bgcolor: '#f5f5f5' },
            width: { xs: 28, md: 36 },
            height: { xs: 28, md: 36 },
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            borderRadius: 10,
            opacity: 0.9
          }}
        >
          <InfoIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
        </IconButton>
      </Box>

      {showLegend && <LegendMap variant="left" />}

      {/*
      {!hasData && !loading && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', zIndex: 2000, pointerEvents: 'none' }}>
          <p style={{ textAlign: 'center', lineHeight: 1.8, opacity: 0.95, fontSize: '0.95rem' }}>
            No storm or forecast data available for <strong>{cqlFecha.split(' ')[0]} {selectedTime} UTC</strong>.<br />
            <span style={{ display: 'block', marginTop: '0.4em', opacity: 0.8 }}>The selected time may be outside the forecast window or no data available.</span>
            <span style={{ display: 'inline-block', marginTop: '0.6em', fontStyle: 'italic', opacity: 0.8 }}>— try adjusting the timeline slider —</span>
          </p>
        </Box>
      )}
      */}
    </Box>
  );
};

export default MapLeft;